<template>
  <div class="rm-screen rm-taken">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>

    <template v-else>
      <div class="rm-summary">
        <div class="rm-summary__row">
          <div class="rm-summary__labels">
            <span>{{ $t("taken") }}</span>
            <span>
              {{ pluralT("report_mobile.companies_n", summary.companies) }} ·
              {{ pluralT("report_mobile.meals_n", summary.ordered) }}
            </span>
          </div>
          <span class="rm-summary__value">
            {{ summary.taken }}<span class="rm-summary__value-of"> / {{ summary.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress rm-progress--inverse"
          role="progressbar"
          :aria-valuenow="summary.taken"
          aria-valuemin="0"
          :aria-valuemax="summary.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${summary.percent}%` }"></div>
        </div>
      </div>

      <article v-for="company in takenCompanies" :key="company.key" class="rm-card rm-taken-card">
        <div class="rm-taken-card__head">
          <div class="rm-taken-card__title">
            <h2 class="rm-taken-card__name">{{ company.name }}</h2>
            <span v-if="company.shiftsText" class="rm-muted">{{ company.shiftsText }}</span>
            <span v-else-if="company.shiftBadges.length" class="rm-badges rm-taken-card__badges">
              <span v-for="badge in company.shiftBadges" :key="badge.id" class="rm-badge rm-num">
                {{ $t("report_mobile.shift_short", { n: badge.id }) }} · {{ badge.taken }} / {{ badge.ordered }}
              </span>
            </span>
          </div>
          <span class="rm-taken-card__value rm-num">
            {{ company.taken }}<span class="rm-taken-card__of"> / {{ company.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress"
          :class="{ 'rm-progress--complete': company.complete }"
          role="progressbar"
          :aria-valuenow="company.taken"
          aria-valuemin="0"
          :aria-valuemax="company.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${company.percent}%` }"></div>
        </div>
        <div class="rm-taken-card__actions">
          <button type="button" class="rm-btn rm-btn--sm" @click="openWorkers(company)">
            <v-icon size="18" color="#1c1917">mdi-account-multiple-outline</v-icon>
            {{ $t("employees") }}
          </button>
          <button
            type="button"
            class="rm-btn rm-btn--sm"
            @click="openDialogReportFirm(company.source)"
          >
            <v-icon size="18" color="#1c1917">mdi-calendar-month-outline</v-icon>
            {{ $t("monthly") }}
          </button>
        </div>
      </article>

      <div v-if="!takenCompanies.length" class="rm-empty">
        {{
          filters.search
            ? $t("report_mobile.no_results_for", { search: filters.search })
            : $t("no_taken_meals_data")
        }}
      </div>
    </template>

    <div v-if="canScan" class="rm-taken__scan">
      <button type="button" class="rm-btn rm-btn--primary rm-taken__scan-btn" @click="openScanner">
        <v-icon size="22" color="#ffffff">mdi-qrcode-scan</v-icon>
        {{ $t("report_mobile.scan_worker_qr") }}
      </button>
    </div>

    <report-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :company-data="firmReportItem"
    ></report-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import ReportMealsTabTaken from "@/components/report/meals/tabs/ReportMealsTabTaken.vue";
import hasShiftBinding from "../../shared/hasShiftBinding";
import hasTakenCompanies from "../../shared/hasTakenCompanies";

const SCANNER_ROUTE = "canteen";

/**
 * Mobilni prikaz "Preuzeto": nasleđuje učitavanje, mesečni dijalog, štampu i Excel
 * iz ReportMealsTabTaken.
 */
export default {
  name: "ReportMobileTaken",
  extends: ReportMealsTabTaken,
  mixins: [hasTakenCompanies, hasShiftBinding],
  computed: {
    canScan() {
      const userType = this.$store.state.user.user?.type;
      const route = this.$router.options.routes.find((item) => item.name === SCANNER_ROUTE);
      return Boolean(route && route.roles?.includes(userType));
    },
  },
  methods: {
    /** "Radnici": otvara listu radnika izabrane firme. */
    openWorkers(company) {
      const id = company.source.id;
      const known = (this.$store.state.report.mealCompanies || []).find((item) => item.id === id);
      this.$store.commit("report/SET_SELECTED_MEAL_COMPANY", known || { id, name: company.name });
      this.$emit("open-screen", "employees");
    },
    openScanner() {
      this.$router.push({ name: SCANNER_ROUTE });
    },
  },
};
</script>

<style lang="scss">
.rm-taken {
  min-height: calc(100vh - 220px);
}

.rm-theme .rm-taken-card {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-size: 18px;
    font-weight: 700;
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    white-space: nowrap;
  }

  &__of {
    font-size: 15px;
    color: var(--rm-muted);
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
}

.rm-taken__scan {
  position: sticky;
  bottom: 0;
  margin: auto -16px -24px;
  padding: 12px 16px calc(20px + env(safe-area-inset-bottom));
  background: var(--rm-bg);
  border-top: 1px solid var(--rm-border);
}

.rm-theme .rm-taken__scan-btn {
  height: 56px;
  border-radius: 16px;
}
</style>
